import { Link } from '../../components/SmartLink';
import {
  CheckCircle, Warning, MapPin, WhatsappLogo,
  Clock, ArrowRight, Shield, Airplane,
  Users, SealCheck, CalendarBlank,
  FileText, Star, Suitcase,
} from '@phosphor-icons/react';
import VisaDetailLayout from '../../components/visas/VisaDetailLayout';
import { VISA_BY_ID } from '../../data/visaData';
import { buildWAUrl } from '../../lib/whatsapp';
import { ExpandableTip, AIAnswerBox } from '../../components/visas/VisaPageHelpers';

const WA_URL = buildWAUrl(
  'Hi Big Bird Travel, I have a long layover at a UAE airport and want to check if I need a transit visa (48 or 96 hour) to leave the airport. My nationality is ____.'
);

const SUMMARY_CARDS = [
  { label: 'Duration Options', value: '48 or 96 Hours', sub: 'Single entry', Icon: CalendarBlank },
  { label: 'For', value: 'Long Layovers', sub: 'DXB · AUH · SHJ airports', Icon: Airplane },
  { label: 'Processing Time', value: '24 – 48 Hours', sub: 'Apply ahead where possible', Icon: Clock },
  { label: 'Requirement', value: 'Confirmed Onward Flight', sub: 'Within the visa window', Icon: FileText },
  { label: 'Not Needed If', value: 'Visa-Free Nationality', sub: 'Many passports qualify', Icon: Shield },
];

const GUIDE_FOR = [
  { Icon: Airplane, label: 'Long-Layover Passengers at Dubai/Abu Dhabi' },
  { Icon: Suitcase, label: 'Travellers Wanting to Leave the Airport' },
  { Icon: Users,    label: 'Nationalities Without Visa-Free UAE Entry' },
  { Icon: Star,     label: 'Passengers on Airlines Supporting Transit Sponsorship' },
  { Icon: MapPin,   label: 'Travellers Wanting a Short Dubai/Abu Dhabi City Tour' },
  { Icon: FileText, label: 'Anyone Unsure If They Need a Visa at All' },
];

const DURATION_TABLE = [
  { duration: '48-Hour Transit Visa', bestFor: 'Layovers under 2 days', badge: 'blue' },
  { duration: '96-Hour Transit Visa', bestFor: 'Layovers up to 4 days — time for a short city visit', badge: 'navy' },
  { duration: 'No Visa Needed', bestFor: 'Nationalities with UAE visa-free / visa-on-arrival access', badge: 'green' },
];

const HIDDEN_FACTS = [
  {
    Icon: Shield,
    title: 'Most travellers don’t actually need a transit visa',
    desc: 'A large number of nationalities already get UAE visa-free entry or visa-on-arrival, which covers a layover without any separate transit visa application. We check your passport first, before recommending anything.',
  },
  {
    Icon: FileText,
    title: 'The onward ticket has to fall inside the visa window',
    desc: 'Your connecting flight must depart within the 48 or 96-hour validity, or the transit visa doesn’t match your itinerary. We check your exact flight times before choosing the duration.',
  },
  {
    Icon: Airplane,
    title: 'You can genuinely see two cities on a long layover',
    desc: 'A 96-hour transit visa is enough time to visit both Abu Dhabi and Dubai on one layover, not just the arrival city — worth planning if your connection is long.',
  },
  {
    Icon: Suitcase,
    title: 'We can arrange the layover, not just the visa',
    desc: 'Airport transfers, a short guided city tour, or even a hotel day-use room can all be booked alongside the transit visa so the layover itself is organised, not just legally possible.',
  },
];

const TRANSIT_SERVICES = [
  { label: 'UAE Visit Visa',        to: '/visas/uae-visit-visa', desc: 'For a longer UAE stay' },
  { label: 'UAE Tours',             to: '/uae-tours',            desc: 'Short city tours & day trips' },
  { label: 'Flights',               to: '/flights',              desc: 'Onward flight booking' },
  { label: 'Hotel Booking',         to: '/hotels',                desc: 'Day-use rooms near the airport' },
  { label: 'All Visa Services',     to: '/visas',                 desc: 'UAE, Schengen, UK, USA & more' },
  { label: 'Contact Our Team',      to: '/contact',               desc: 'Mussafah, Abu Dhabi office' },
];

const SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'UAE Transit Visa Assistance (48/96 Hour)',
  description: 'UAE transit visa assistance for layover passengers at Abu Dhabi and Dubai airports — 48 and 96-hour stopover options. Based in Mussafah, Abu Dhabi.',
  url: 'https://bigbirdtravel.com/visas/transit-visa',
  provider: {
    '@type': 'TravelAgency',
    name: 'Big Bird Travel & Tourism',
    url: 'https://bigbirdtravel.com',
    telephone: '+971561789642',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Al Sawari Street 5, Building No-21, Office No-4, M-13',
      addressLocality: 'Mussafah',
      addressRegion: 'Abu Dhabi',
      addressCountry: 'AE',
    },
    email: 'info@bigbirdtravel.com',
    openingHours: 'Mo-Sa 09:00-18:00',
  },
  areaServed: { '@type': 'Country', name: 'United Arab Emirates' },
  serviceType: 'UAE Transit Visa',
  termsOfService: 'https://bigbirdtravel.com/terms',
};

function TransitVisaExtraSections() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />

      <section className="py-10 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-4">UAE Transit Visa — At a Glance</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SUMMARY_CARDS.map(({ label, value, sub, Icon }) => (
              <div key={label} className="rounded-2xl border border-[#C3DCF5] bg-white p-4 shadow-sm flex flex-col gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={16} weight="fill" className="text-[#0068B3]" />
                </span>
                <p className="text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{label}</p>
                <p className="font-display font-black text-[#071126] text-[14px] leading-tight">{value}</p>
                <p className="text-[11px] text-gray-400 leading-tight">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-8 bg-white border-b border-gray-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 items-start">
          <div className="shrink-0 lg:w-60">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">About This Guide</span>
            <h2 className="font-display font-black text-[#071126] text-[20px] leading-tight">Who this is for</h2>
            <p className="text-gray-500 text-[13px] mt-2 leading-relaxed">
              For passengers with a long layover at a UAE airport who want to leave the airport instead of waiting inside.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GUIDE_FOR.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 rounded-xl border border-[#D8EAF7] bg-[#F5FAFF] px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EAF7FF] border border-[#C3DCF5]">
                  <Icon size={14} weight="fill" className="text-[#0068B3]" />
                </span>
                <span className="text-[13px] font-semibold text-[#071126]">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
        <AIAnswerBox
          question="Do I need a visa to leave a UAE airport during a layover?"
          answer="It depends on your nationality. Many passport holders already get UAE visa-free entry or visa-on-arrival, which covers a layover without any separate transit visa. If your nationality is not on those lists, a 48-hour or 96-hour transit visa lets you leave the airport, provided you have a confirmed onward flight within that window. Big Bird Travel, based in Mussafah, Abu Dhabi, checks a traveller's nationality and itinerary before recommending anything."
        />
        <AIAnswerBox
          question="What is the difference between a 48-hour and 96-hour UAE transit visa?"
          answer="Both let a passenger leave the airport during a layover — the difference is duration. A 48-hour visa suits layovers under two days, while a 96-hour visa covers up to four days, enough time to visit both Abu Dhabi and Dubai. The right choice depends on the traveller's actual layover length between inbound and onward flights."
        />
      </div>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Which Option Fits Your Layover</span>
          <h2 className="font-display font-black text-[#071126] mb-2" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.65rem)' }}>
            UAE transit visa options — 2026
          </h2>
          <p className="text-gray-500 text-[13px] mb-6 max-w-2xl">
            Confirm your nationality and flight times with us before booking anything.
          </p>
          <div className="overflow-x-auto rounded-2xl border border-[#D8EAF7] shadow-sm">
            <table className="w-full text-left text-[13px] min-w-[550px]">
              <thead>
                <tr className="bg-[#071126] text-white">
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tl-2xl">Option</th>
                  <th className="px-5 py-3.5 font-black text-[11px] uppercase tracking-wider rounded-tr-2xl">Best For</th>
                </tr>
              </thead>
              <tbody>
                {DURATION_TABLE.map((row, i) => (
                  <tr key={row.duration} className={`border-t border-[#E8F3FC] ${i % 2 === 0 ? 'bg-white' : 'bg-[#F5FAFF]'}`}>
                    <td className="px-5 py-3.5 font-semibold text-[#071126]">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide whitespace-nowrap mr-2 ${
                        row.badge === 'green' ? 'bg-green-100 text-green-800' :
                        row.badge === 'blue'  ? 'bg-[#EAF7FF] text-[#0068B3]' :
                                                'bg-[#071126] text-white'
                      }`}>{row.duration}</span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-600">{row.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 px-6 lg:px-8 bg-amber-50 border-y border-amber-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-700 mb-3 block">⚠ Know Before You Apply</span>
            <h2 className="font-display font-black text-[#071126] mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Don't pay for a visa you don't need
            </h2>
            <p className="text-gray-700 text-[15px] leading-relaxed mb-4">
              The most common mistake is applying for a transit visa without checking if your nationality already has visa-free or visa-on-arrival access — in which case no application is needed at all.
            </p>
            <div className="space-y-2.5 mt-6">
              <ExpandableTip title="Check visa-free eligibility first">
                Many passports already get UAE visa-free entry or visa-on-arrival, which is enough for a layover. We always check this first before recommending a paid transit visa.
              </ExpandableTip>
              <ExpandableTip title="Match the visa duration to your real layover">
                Choosing 48 hours when your layover is closer to 4 days (or vice versa) wastes money or leaves you short. Send your exact flight times so we pick correctly.
              </ExpandableTip>
              <ExpandableTip title="Apply a few days ahead where you can">
                While it can sometimes be arranged within 24–48 hours, applying earlier removes any risk if there's a delay in document processing before your flight.
              </ExpandableTip>
              <ExpandableTip title="Plan the layover itself, not just the visa">
                A long layover is more useful with a plan — airport transfer, a short city tour, or a day-use hotel room. Ask us to arrange this alongside the visa.
              </ExpandableTip>
            </div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle size={18} weight="fill" className="text-[#0068B3]" />
              <span className="font-black text-[12px] uppercase tracking-widest text-[#0068B3]">Quick Layover Check</span>
            </div>
            <ul className="space-y-3 mb-5">
              {[
                { text: 'Layover under 2 days', result: '48-hour transit visa', type: 'ok' },
                { text: 'Layover 2–4 days', result: '96-hour transit visa', type: 'ok' },
                { text: 'Not sure if my nationality needs one', result: 'Message your passport & flight', type: 'warn' },
                { text: 'Want to see 2 cities on the layover', result: 'Go with 96-hour', type: 'ok' },
              ].map(item => (
                <li key={item.text} className="flex items-start gap-2.5">
                  {item.type === 'ok' && <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0 mt-0.5" />}
                  {item.type === 'warn' && <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
                  <span className="text-[12px] text-gray-700"><strong>{item.text}</strong> → {item.result}</span>
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0F7A35] text-white text-[13px] font-black py-3.5 hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Check My Layover — Free
            </a>
            <p className="text-center text-[11px] text-gray-400 mt-2.5 leading-snug">
              Send your nationality & flight times — honest answer, same day.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#071126] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#C9A227] mb-3 block">Authority Section</span>
          <h2 className="font-display font-black text-white mb-3" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
            What most transit visa pages don't tell you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIDDEN_FACTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-[#0096FF]/20 bg-white/[0.045] p-5 hover:border-[#0096FF]/40 hover:bg-white/[0.06] transition-all">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0096FF]/10 border border-[#0096FF]/20 mb-4">
                  <Icon size={17} weight="fill" className="text-[#0096FF]" />
                </span>
                <h3 className="font-display font-black text-white text-[13px] mb-2 leading-tight">{title}</h3>
                <p className="text-blue-50/60 text-[12px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-[#0096FF]/25 bg-white/[0.04] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-display font-black text-white text-[15px] mb-1">Got a long layover coming up?</p>
              <p className="text-blue-50/60 text-[13px]">Send your nationality and flight times. We'll confirm if you need a visa at all — usually within the hour.</p>
            </div>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-5 py-3 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={17} weight="fill" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-14 px-6 lg:px-8 bg-[#041024] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(0,150,255,0.14),transparent_50%)]" />
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0096FF] mb-3 block">Why Big Bird</span>
            <h2 className="font-display font-black text-white mb-4" style={{ fontSize: 'clamp(1.4rem, 2.2vw, 1.85rem)' }}>
              Turn a layover into a mini UAE trip
            </h2>
            <p className="text-blue-50/85 text-[15px] italic leading-relaxed mb-5 border-l-2 border-[#0096FF] pl-4">
              "Send your nationality and flight times. We'll confirm if you need a visa, arrange it fast, and even plan your layover itinerary."
            </p>
            <p className="text-blue-50/70 text-[14px] leading-relaxed mb-4">
              We're a licensed travel agency in <strong className="text-white">Mussafah, Abu Dhabi</strong>. For transit visas we:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Check whether your nationality needs a visa at all.',
                'Match the 48 or 96-hour option to your real layover.',
                'Prepare and issue the visa ahead of your travel date.',
                'Arrange airport transfers and a short city tour if wanted.',
                'Advise if a longer UAE visit visa suits you better.',
              ].map(item => (
                <li key={item} className="flex items-start gap-3 text-[13px] text-blue-50/80">
                  <CheckCircle size={14} weight="fill" className="text-[#0096FF] shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0F7A35] text-white text-[13px] font-black px-6 py-3.5 rounded-xl hover:bg-[#0a6029] transition-colors">
              <WhatsappLogo size={19} weight="fill" /> WhatsApp Us — Same-Day Response
            </a>
          </div>
          <div className="space-y-4">
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-3xl p-6">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/70 mb-4">Big Bird Travel — Agency Details</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Office',   value: 'Mussafah 13, Abu Dhabi' },
                  { label: 'WhatsApp', value: '+971 56 178 9642' },
                  { label: 'License',  value: 'ADRA CN-5285999' },
                  { label: 'Response', value: 'Same day' },
                  { label: 'Hours',    value: 'Mon–Sat 9am–6pm UAE' },
                  { label: 'Serves',   value: 'Abu Dhabi · Dubai · UAE' },
                ].map(item => (
                  <div key={item.label}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0096FF]/60 mb-0.5">{item.label}</p>
                    <p className="text-[13px] font-semibold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/[0.05] border border-[#0096FF]/20 rounded-2xl p-5">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-[#C9A227]" />))}
                <span className="text-[11px] text-blue-50/50 ml-2">Verified customer</span>
              </div>
              <blockquote className="text-[13px] text-blue-50/80 italic leading-relaxed">
                "18-hour layover in Dubai turned into an actual mini trip. They sorted the visa and a quick city tour before my next flight."
              </blockquote>
              <p className="text-[11px] text-blue-50/40 mt-2">— Transit passenger, DXB</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-3 block">Related Services</span>
          <h2 className="font-display font-black text-[#071126] mb-6 text-[20px]">
            Layover sorted. Make the most of your time.
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {TRANSIT_SERVICES.map(({ label, to, desc }) => (
              <Link key={label} to={to} className="group rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4 hover:border-[#0096FF]/50 hover:bg-[#EAF7FF] hover:-translate-y-0.5 transition-all flex flex-col gap-2">
                <ArrowRight size={15} weight="bold" className="text-[#0068B3] group-hover:translate-x-0.5 transition-transform" />
                <p className="font-display font-black text-[#071126] text-[13px] leading-tight">{label}</p>
                <p className="text-[11px] text-gray-400">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-7 px-6 lg:px-8 bg-[#F5FAFF] border-b border-[#D8EAF7]">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><CalendarBlank size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Last Updated:</strong> September 2026</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><SealCheck size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Reviewed by:</strong> Big Bird Travel Visa Team</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><Shield size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">License:</strong> ADRA CN-5285999</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin size={13} weight="fill" className="text-[#0068B3]" /><span><strong className="text-[#071126]">Office:</strong> Mussafah 13, Abu Dhabi, UAE</span></div>
          <div className="flex items-center gap-2 text-[12px] text-gray-500"><WhatsappLogo size={13} weight="fill" className="text-green-600" /><span><strong className="text-[#071126]">WhatsApp:</strong> Same-day response</span></div>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="shrink-0">
              <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#0068B3] mb-2 block">Local Service</span>
              <h2 className="font-display font-black text-[#071126] text-[18px]">Airports We Serve</h2>
            </div>
            <div className="flex-1">
              <p className="text-gray-500 text-[14px] mb-4">
                We assist layover travellers connecting through <strong className="text-[#071126]">Abu Dhabi (AUH), Dubai (DXB) and Sharjah (SHJ)</strong> — from our Mussafah office.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Abu Dhabi Airport (AUH)', 'Dubai Airport (DXB)', 'Sharjah Airport (SHJ)', 'Mussafah', 'Abu Dhabi City', 'Dubai City'].map(area => (
                  <span key={area} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EAF7FF] border border-[#C3DCF5] text-[12px] font-black text-[#0068B3]">
                    <MapPin size={11} weight="fill" />{area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function TransitVisa() {
  return (
    <VisaDetailLayout
      visa={VISA_BY_ID['transit-visa']}
      extraSections={<TransitVisaExtraSections />}
    />
  );
}
